import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle } from "lucide-react";
import SEO from "./SEO";
import BlockRenderer from "./cms/BlockRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified CMS-driven public page renderer.
 *
 * Fetches /api/website/pages/{slug} (only published) plus optional tariffs.
 * Falls back to `fallback` (a JSX element) when the CMS returns no published
 * blocks, mirroring the proven luchtvracht/zeevracht safety pattern.
 *
 * Props:
 *  - slug:         CMS slug to fetch (string, required)
 *  - fallback:     JSX rendered when CMS is empty / archived / unreachable
 *  - testid:       optional `data-testid` for the CMS-driven container
 *  - withTariffs:  if true, also fetch /api/website/tariffs?page_slug={slug}
 *  - topSection:   optional JSX rendered ABOVE the CMS blocks (e.g. PageBanner)
 *                  — always shown, even when fallback would otherwise kick in.
 *  - extraSections: optional JSX appended after CMS blocks (e.g. quote form)
 *  - hideBlockTypes: optional array of block-type keys to strip BEFORE
 *                    rendering (e.g. ["hero"] to avoid a duplicate hero
 *                    when `topSection` already provides one).
 */
export default function CmsPageRenderer({
  slug,
  fallback,
  testid = "cms-driven",
  withTariffs = false,
  topSection = null,
  extraSections = null,
  hideBlockTypes = null,
  breadcrumbs = null,
  requiredBlock = null,
}) {
  const [page, setPage] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let active = true;
    const calls = [
      axios.get(`${BACKEND_URL}/api/website/pages/${slug}`).catch((err) => {
        if (err?.response?.status === 404) return { data: null, _missing: true };
        return { data: null };
      }),
    ];
    if (withTariffs) {
      calls.push(
        axios
          .get(`${BACKEND_URL}/api/website/tariffs-with-options?page_slug=${slug}`)
          .catch(() => ({ data: [] }))
      );
    }
    Promise.all(calls)
      .then((res) => {
        if (!active) return;
        const p = res[0];
        if (p?._missing) setMissing(true);
        setPage(p?.data || null);
        if (withTariffs) setTariffs(Array.isArray(res[1]?.data) ? res[1].data : []);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug, withTariffs]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid={`${testid}-loading`}>
        <div className="text-sm text-gray-500 font-chivo uppercase tracking-[0.2em]">Laden…</div>
      </div>
    );
  }

  const allBlocks = page?.content?.blocks || [];
  const hiddenSet = Array.isArray(hideBlockTypes) && hideBlockTypes.length > 0
    ? new Set(hideBlockTypes)
    : null;
  const blocks = hiddenSet
    ? allBlocks.filter((b) => !hiddenSet.has(b.type))
    : allBlocks;
  const useFallback =
    missing || !page || page.status !== "published" || blocks.length === 0;

  if (useFallback) {
    return (
      <>
        {topSection}
        {breadcrumbs}
        {process.env.NODE_ENV !== "production" && (
          <div
            className="bg-orange-50 border-b border-orange-200 py-2 px-4 text-xs text-orange-700 flex items-center gap-2 justify-center"
            data-testid={`${testid}-fallback-banner`}
          >
            <AlertCircle className="w-4 h-4" />
            <span>Statische fallback in gebruik (geen gepubliceerde CMS-versie voor /{slug}).</span>
          </div>
        )}
        {fallback}
        {requiredBlock?.element}
      </>
    );
  }

  const seo = page.seo || {};
  return (
    <div data-testid={testid}>
      <SEO
        title={seo.title}
        description={seo.meta_description}
        noindex={!!seo.no_index}
      />
      {topSection}
      {breadcrumbs}
      <BlockRenderer blocks={blocks} tariffs={tariffs} slug={slug} />
      {requiredBlock && !blocks.some(block => block.type === requiredBlock.type) && requiredBlock.element}
      {extraSections}
    </div>
  );
}
